import React, { useState, useRef } from "react";
import {
  Box,
  Fab,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
  Grid,
  Card,
  CardMedia,
  IconButton,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { fetchTodayPhotoLength } from "../Hooks/Api";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { TokenAtom, TokenInfo, NewPhotoStates } from "../Hooks/TokenAtom";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";

import { createTheme, ThemeProvider } from "@mui/material/styles";

const compressImage = (file) => {
  return new Promise((resolve) => {
    if (file.size < 300 * 1024) {
      resolve(file);
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => resolve(file); // 읽기 실패 시 원본 사용
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => resolve(file); // 디코딩 실패 시 원본 사용
      img.src = e.target.result;
      img.onload = () => {
        const MAX_SIZE = 1920;
        let { width, height } = img;
        if (width > MAX_SIZE || height > MAX_SIZE) {
          if (width >= height) {
            height = Math.round((height * MAX_SIZE) / width);
            width = MAX_SIZE;
          } else {
            width = Math.round((width * MAX_SIZE) / height);
            height = MAX_SIZE;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() }));
            } else {
              resolve(file); // toBlob 실패 시 원본 사용
            }
          },
          "image/jpeg",
          0.85
        );
      };
    };
  });
};



const UploadButtonDiary = ({ onUploadSuccess }) => {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");

  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedDate, setSelectedDate] = useState(dayjs()); // 날짜 상태 추가
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const tokens = useRecoilValue(TokenAtom);
  const tokenInfo = useRecoilValue(TokenInfo);
  const setTodayPhotosCount = useSetRecoilState(NewPhotoStates);
  const diaryRef = useRef();
  const titleRef = useRef();
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    files.forEach((item) => URL.revokeObjectURL(item.preview));
    setFiles([]);
    if (diaryRef.current) {
      diaryRef.current.value = "";
    }
    if (titleRef.current) {
      titleRef.current.value = "";
    }
    setOpen(false);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setDatePickerOpen(false); // 날짜 선택 시 DatePicker 닫기
  };

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    const newItems = newFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setFiles((prev) => [...prev, ...newItems]);
  };

  const handleUpload = async () => {
    // ref는 async 이후 unmount되므로 반드시 먼저 읽어야 함
    const titleValue = titleRef.current ? titleRef.current.value : "";
    const diaryValue = diaryRef.current ? diaryRef.current.value : "";

    setLoading(true); // 로딩 상태 시작
    setLoadingMsg("이미지 압축 중...");

    // 메모리 과부하 방지를 위해 순차 처리
    const compressedFiles = [];
    for (let i = 0; i < files.length; i++) {
      setLoadingMsg(`압축 중... (${i + 1}/${files.length})`);
      const compressed = await compressImage(files[i].file);
      compressedFiles.push(compressed);
    }

    setLoadingMsg("업로드 중...");
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("writer", tokenInfo.first_name);
    formData.append("title", titleValue);
    formData.append("diary", diaryValue);
    formData.append("date", selectedDate.format("YYYY-MM-DD"));
    formData.append("dateToday", dayjs().format("YYYY-MM-DD")); // 선택된 날짜 추가

    compressedFiles.forEach((file) => {
      formData.append("image", file);
    });

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded * 100) / event.total);
        setUploadProgress(percentComplete);
      }
    });

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        setLoading(false); // 로딩 상태 종료
        if (xhr.status === 201) {
          onUploadSuccess(xhr.status); // 업로드 성공 시 호출
          setFiles([]);
          setUploadProgress(0);
          fetchTodayPhotoLength(tokens, setTodayPhotosCount);
          handleClose();
        } else {
          onUploadSuccess(xhr.status); // 업로드 성공 시 호출
          console.error("Error uploading files:", xhr.responseText);
          setFiles([]);
          setUploadProgress(0);

          handleClose();
        }
      }
    };

    xhr.open("POST", `/api/UploadPhotoDiary`, true);
    xhr.setRequestHeader("Authorization", `Bearer ${tokens.access}`);
    xhr.send(formData);
  };

  const handleRemove = (itemToRemove) => {
    URL.revokeObjectURL(itemToRemove.preview);
    setFiles((prev) => prev.filter((item) => item !== itemToRemove));
  };
  const pickerTheme = createTheme({
    palette: {
      primary: { main: "#8c6744" },
      secondary: { main: "#cf9e62" },
    },
    typography: {
      fontFamily: "LeeSeoyun, sans-serif",
    },
  });

  return (
    <Box sx={{ position: "fixed", right: 16, bottom: 32 }}>
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleClickOpen}
        size="small"
        sx={{
          background: "linear-gradient(45deg, #8c6744, #cf9e62)",
          color: "white",
          "&:hover": {
            background: "linear-gradient(45deg, #a67855, #f8e5d0)",
          },
        }} // 캘린더와 동일한 높이 설정
      >
        <AddIcon />
      </Fab>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#f5f4f2", // 전체 배경 색
            borderRadius: "12px", // 모서리 둥글게
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", color: "#8c6744" }}>
          이미지 업로드
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#f5f4f2", pt: "16px !important" }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 4,
              }}
            >
              {loadingMsg.startsWith("압축") ? (
                <CircularProgress />
              ) : (
                <CircularProgress variant="determinate" value={uploadProgress} />
              )}
              <Typography variant="body2" sx={{ mt: 2 }}>
                {loadingMsg.startsWith("압축")
                  ? loadingMsg
                  : `${uploadProgress}%`}
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1, // 버튼 간 간격
                }}
              >
                <TextField
                  type="file"
                  fullWidth
                  onChange={handleFileChange}
                  inputProps={{ accept: "image/*", multiple: true }}
                  sx={{ display: "none" }}
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="contained"
                    component="span"
                    sx={{
                      minWidth: 0,
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "#B8860B", // 민트 → 초록
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      "&:hover": {
                        background: "#DAA520", // hover 버전
                      },
                    }}
                  >
                    <AddPhotoAlternateIcon
                      sx={{ fontSize: 20, color: "white" }}
                    />
                  </Button>
                </label>
                <ThemeProvider theme={pickerTheme}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="YYYY-MM-DD"
                      label="날짜 선택"
                      value={selectedDate}
                      onChange={handleDateChange}
                      open={datePickerOpen}
                      onOpen={() => setDatePickerOpen(true)}
                      onClose={() => setDatePickerOpen(false)}
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: {
                            width: "100%",
                            minHeight: "40px", // 높이 최소 확보
                            backgroundColor: "white", // ← 폼 배경 하얗게
                            borderRadius: "8px", // ← 둥근 모서리(선택)

                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#8c6744",
                            },

                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#8c6744",
                            },

                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#8c6744",
                            },
                            "& .MuiInputBase-input": {
                              backgroundColor: "white",
                              color: "#8c6744",
                            },
                            "& .MuiFormLabel-root": {
                              color: "#8c6744",
                            },
                            "& .MuiFormLabel-root.Mui-focused": {
                              color: "#8c6744", // 예: 강조색
                            },
                            "& .MuiSvgIcon-root path": {
                              fill: "#8c6744",
                            },
                          },
                        },
                        actionBar: {
                          actions: ["today"],
                        },
                      }}
                    />
                  </LocalizationProvider>
                </ThemeProvider>
              </Box>
              {/* 첨부 사진 개수 (🟦 두 번째 줄) */}
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontSize: "0.8rem", color: "#8c6744" }}
                >
                  첨부 사진: {files.length}개 {files.length > 0 && `(${(files.reduce((sum, item) => sum + item.file.size, 0) / 1024 / 1024).toFixed(1)}MB)`}
                </Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <TextField
                  label="제목"
                  fullWidth
                  inputRef={titleRef}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f4f2",
                      "& fieldset": {
                        borderColor: "#c9b08a",
                      },
                      "&:hover fieldset": {
                        borderColor: "#b89263",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#8c6744",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#8c6744",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#8c6744",
                    },
                    /* ✨ 입력 텍스트 색상 변경 */
                    "& .MuiInputBase-input": {
                      color: "#8c6744", // ← 원하는 텍스트 색상
                    },
                  }}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="메모"
                  fullWidth
                  multiline
                  rows={3}
                  inputRef={diaryRef}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f4f2",
                      "& fieldset": {
                        borderColor: "#c9b08a",
                      },
                      "&:hover fieldset": {
                        borderColor: "#b89263",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#8c6744",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#8c6744",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#8c6744",
                    },
                    /* ✨ 입력 텍스트 색상 변경 */
                    "& .MuiInputBase-input": {
                      color: "#8c6744", // ← 원하는 텍스트 색상
                    },
                  }}
                />
              </Box>
              <Box>
                <Grid container spacing={2}>
                  {files.length === 0 ? (
                    <Box sx={{ width: "100%", textAlign: "center", py: 2 }}>
                      <Typography variant="body2" sx={{ color: "#8c6744" }}>
                        업로드할 이미지가 없습니다.
                      </Typography>
                    </Box>
                  ) : (
                    files.map((item, index) => (
                      <Grid item xs={4} key={index}>
                        <Card sx={{ position: "relative" }}>
                          <CardMedia
                            component="img"
                            image={item.preview}
                            alt={item.file.name}
                            sx={{
                              height: 100, // 카드 높이에 맞춰 조절 가능
                              width: "100%", // 카드 폭 꽉 채우기
                              objectFit: "cover", // 사진 전체 영역 채우기
                            }}
                          />
                          <IconButton
                            onClick={() => handleRemove(item)}
                            sx={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              backgroundColor: "rgba(184, 134, 11, 0.6)", // 반투명 골드
                              color: "white", // 아이콘 색상
                              width: 28,
                              height: 28,
                              "&:hover": {
                                backgroundColor: "#B8860B", // hover 시 진한 골드
                              },
                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Card>
                      </Grid>
                    ))
                  )}
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleUpload}
            variant="contained"
            sx={{
              background: "#B8860B", // 민트 → 초록
              color: "white",
              fontWeight: "bold",
              "&:hover": {
                background: "#DAA520", // hover 버전
              },
            }}
          >
            업로드
          </Button>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              color: "#B8860B",
              borderColor: "#B8860B", // ← outlined 라서 border도 맞춰줘야 함
              "&:hover": {
                borderColor: "#DAA520",
                background: "#DAA520",
              },
            }}
          >
            취소
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadButtonDiary;
